import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AccountUserInfoTutorial } from '../../api/models';

export interface Tutorial {
	type: string;
	dismissed: boolean;
}

interface FtuxState {
	// Currently running FTUX state
	run: boolean;
	paused: boolean;
	stepIndex: number;
	activeFtuxId: string | null;
	currentTarget: string | null; // The target selector being highlighted (e.g., 'weekly-plan-navigation')

	// User's FTUX completion status (from API)
	onboardingCompleted: boolean;
	tutorials: AccountUserInfoTutorial[];

	// Loading state
	isLoading: boolean;
}

const initialState: FtuxState = {
	run: false,
	paused: false,
	stepIndex: 0,
	activeFtuxId: null,
	currentTarget: null,
	onboardingCompleted: false,
	tutorials: [],
	isLoading: false
};

// using Redux to store some FTUX state, so components can know if it's running
// say we're on the eLibrary page... if FTUX is running, we may want to open the first accordion and highlight the book
// need to read from a global store to tell if it's running so we can do that
export const FtuxSlice = createSlice({
	name: 'ftux',
	initialState,
	reducers: {
		begin: (state, action: PayloadAction<{ ftuxId?: string }>) => {
			state.run = true;
			state.paused = false; // ← Reset pause state
			state.stepIndex = 0;
			state.activeFtuxId = action.payload?.ftuxId || null;
			state.currentTarget = null;
		},

		pause: (state) => {
			state.paused = true;
		},

		resume: (state) => {
			state.paused = false;
		},

		// Update the current target being highlighted
		setCurrentTarget: (state, action: PayloadAction<string | null>) => {
			state.currentTarget = action.payload;
		},

		goNext: (state) => {
			state.stepIndex = state.stepIndex + 1;
		},

		goBack: (state) => {
			state.stepIndex = state.stepIndex > 0 ? state.stepIndex - 1 : 0;
		},

		cancel: (state) => {
			state.run = false;
			state.paused = false; // ← Reset pause state
			state.stepIndex = 0;
			state.activeFtuxId = null;
			state.currentTarget = null;
		},

		setUserFtuxStatus: (
			state,
			action: PayloadAction<{
				onboardingCompleted: boolean;
				tutorials: AccountUserInfoTutorial[];
			}>
		) => {
			state.onboardingCompleted = action.payload.onboardingCompleted;
			state.tutorials = action.payload.tutorials;
		},

		// Mark onboarding as completed
		completeOnboarding: (state) => {
			state.onboardingCompleted = true;
		},

		// Reset a tutorial (allow it to show again)
		resetTutorial: (state, action: PayloadAction<string>) => {
			const tutorialType = action.payload;
			const tutorial = state.tutorials.find((t) => t.type === tutorialType);
			if (tutorial) {
				tutorial.dismissed = false;
			}
		},

		// Dismiss a tutorial (mark as dismissed)
		dismissTutorial: (state, action: PayloadAction<string>) => {
			const tutorialType = action.payload;
			const tutorial = state.tutorials.find((t) => t.type === tutorialType);
			if (tutorial) {
				tutorial.dismissed = true;
			} else {
				// If tutorial doesn't exist in state yet, add it
				state.tutorials.push({
					type: tutorialType as AccountUserInfoTutorial['type'],
					dismissed: true
				});
			}
		},

		// Set loading state
		setLoading: (state, action: PayloadAction<boolean>) => {
			state.isLoading = action.payload;
		}
	}
});

// Selectors for easy access to FTUX state
export const ftuxSelectors = {
	// Check if FTUX is currently running
	isRunning: (state: { ftux: FtuxState }) => state.ftux.run,

	isPaused: (state: { ftux: FtuxState }) => state.ftux.paused,

	shouldRender: (state: { ftux: FtuxState }) => state.ftux.run && !state.ftux.paused,

	// Get active FTUX ID
	getActiveFtuxId: (state: { ftux: FtuxState }) => state.ftux.activeFtuxId,

	// Get current target being highlighted (e.g., 'weekly-plan-navigation')
	getCurrentTarget: (state: { ftux: FtuxState }) => state.ftux.currentTarget,

	// Get current step index
	getCurrentStepIndex: (state: { ftux: FtuxState }) => state.ftux.stepIndex,

	// Check if onboarding is completed
	isOnboardingCompleted: (state: { ftux: FtuxState }) => state.ftux.onboardingCompleted,

	// Check if a specific tutorial has been dismissed
	isTutorialDismissed: (tutorialType: string) => (state: { ftux: FtuxState }) => {
		const tutorial = state.ftux.tutorials.find((t) => t.type === tutorialType);
		return tutorial?.dismissed ?? false;
	},

	// Get all tutorials
	getTutorials: (state: { ftux: FtuxState }) => state.ftux.tutorials,

	// Check if loading
	isLoading: (state: { ftux: FtuxState }) => state.ftux.isLoading,

	// Check if should show onboarding (not completed and not currently running)
	shouldShowOnboarding: (state: { ftux: FtuxState }) => !state.ftux.onboardingCompleted && !state.ftux.run,

	// Check if should show a specific tutorial
	shouldShowTutorial: (tutorialType: string) => (state: { ftux: FtuxState }) => {
		const tutorial = state.ftux.tutorials.find((t) => t.type === tutorialType);
		return state.ftux.onboardingCompleted && !(tutorial?.dismissed ?? false);
	}
};
